import React from "react";
import {
  User,
  Clipboard,
  Globe,
  Layers,
  Server,
  Shield,
  ReceiptEuro,
  CreditCard,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useCase } from "../../context/CaseContext";

export default function ClientInfoPanel() {
  const {
    clientName,
    setClientName,
    agencyName,
    setAgencyName,
    domain,
    setDomain,
    contactName,
    setContactName,
    contactEmail,
    setContactEmail,
    webDevDetails,
    setWebDevDetails,
    billingDetails,
    setBillingDetails,
    billingMethod,
    setBillingMethod,
    chargebeeCustomerId,
    setChargebeeCustomerId,
    serviceType,
    setServiceType,
    websiteType,
    setWebsiteType,
    wantsNotion,
    setWantsNotion,
    containerId,
    setContainerId,
    dataClassification,
    setDataClassification,
    currentCaseId,
    user,
    savePartial,
    debouncedSave,
    handleTextChange,
    logAuditAction,
  } = useCase();

  const handleBillingChange = (field: string, value: string) => {
    const newDetails = { ...billingDetails, [field]: value };
    setBillingDetails(newDetails);
    debouncedSave(`billing.details.${field}`, value);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
      {/* LEFT COLUMN: CONTACT + PROJECT DEFINITION */}
      <div className="flex flex-col gap-6">
        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5 flex items-center gap-2 pb-2 border-b dark:border-slate-700">
            <User size={14} /> Contact Information
          </h3>

          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-1">
              <label className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400">
                Company
              </label>
              <input
                className="w-full text-sm font-semibold border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                placeholder="Client Name"
                id="clientNameInput"
                value={clientName}
                onChange={(e) =>
                  handleTextChange(setClientName, "clientName", e.target.value)
                }
                onBlur={(e) => {
                  if (e.target.value && e.target.value !== "") {
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: e.target.value,
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "clientName",
                          oldValue: null,
                          newValue: e.target.value,
                        },
                      ],
                    );
                  }
                }}
              />
            </div>
            <div className="grid grid-cols-1 gap-1">
              <label className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400">
                Agency / Partner
              </label>
              <input
                className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                placeholder="Marketing Agency (Optional)"
                id="agencyNameInput"
                value={agencyName}
                onChange={(e) =>
                  handleTextChange(setAgencyName, "agencyName", e.target.value)
                }
              />
            </div>
            <div className="grid grid-cols-1 gap-1">
              <label className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400">
                Website
              </label>
              <div className="relative">
                <Globe
                  size={14}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
                <input
                  className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  placeholder="domain.com"
                  value={domain}
                  onChange={(e) =>
                    handleTextChange(setDomain, "domain", e.target.value)
                  }
                  onBlur={(e) => {
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: clientName || "Unknown",
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "domain",
                          oldValue: null,
                          newValue: e.target.value,
                        },
                      ],
                    );
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400 mb-1 block">
                  Contact Person
                </label>
                <input
                  className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  placeholder="Name"
                  value={contactName}
                  onChange={(e) =>
                    handleTextChange(
                      setContactName,
                      "contactName",
                      e.target.value,
                    )
                  }
                  onBlur={(e) => {
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: clientName || "Unknown",
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "contactName",
                          oldValue: null,
                          newValue: e.target.value,
                        },
                      ],
                    );
                  }}
                />
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold text-gray-500 dark:text-gray-400 mb-1 block">
                  Email
                </label>
                <input
                  className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  placeholder="Email"
                  value={contactEmail}
                  onChange={(e) =>
                    handleTextChange(
                      setContactEmail,
                      "contactEmail",
                      e.target.value,
                    )
                  }
                  onBlur={(e) => {
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: clientName || "Unknown",
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "contactEmail",
                          oldValue: null,
                          newValue: e.target.value,
                        },
                      ],
                    );
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5 flex items-center gap-2 pb-2 border-b dark:border-slate-700">
            <Layers size={14} /> Project Definition
          </h3>
          <div className="space-y-4">
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                Service Type
              </label>
              <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-lg">
                {["implementation", "audit", "additional"].map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      const old = serviceType;
                      setServiceType(t);
                      savePartial({ serviceType: t });
                      if (old !== t) {
                        logAuditAction(
                          "UPDATE",
                          {
                            collection: "onboarding_cases",
                            id: currentCaseId || "",
                            name: clientName || "Unknown",
                          },
                          {
                            uid: user?.uid || "unknown",
                            email: user?.email || "unknown",
                          },
                          [
                            {
                              field: "serviceType",
                              oldValue: old,
                              newValue: t,
                            },
                          ],
                        );
                      }
                    }}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${serviceType === t ? "bg-white dark:bg-slate-700 shadow text-blue-600 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                  Website Type
                </label>
                <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-lg">
                  <button
                    onClick={() => {
                      const old = websiteType;
                      setWebsiteType("ecommerce");
                      savePartial({ websiteType: "ecommerce" });
                      if (old !== "ecommerce") {
                        logAuditAction(
                          "UPDATE",
                          {
                            collection: "onboarding_cases",
                            id: currentCaseId || "",
                            name: clientName || "Unknown",
                          },
                          {
                            uid: user?.uid || "unknown",
                            email: user?.email || "unknown",
                          },
                          [
                            {
                              field: "websiteType",
                              oldValue: old,
                              newValue: "ecommerce",
                            },
                          ],
                        );
                      }
                    }}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${websiteType === "ecommerce" ? "bg-white dark:bg-slate-700 shadow text-blue-600 dark:text-white" : "text-gray-500"}`}
                  >
                    E-Com
                  </button>
                  <button
                    onClick={() => {
                      const old = websiteType;
                      setWebsiteType("leadgen");
                      savePartial({ websiteType: "leadgen" });
                      if (old !== "leadgen") {
                        logAuditAction(
                          "UPDATE",
                          {
                            collection: "onboarding_cases",
                            id: currentCaseId || "",
                            name: clientName || "Unknown",
                          },
                          {
                            uid: user?.uid || "unknown",
                            email: user?.email || "unknown",
                          },
                          [
                            {
                              field: "websiteType",
                              oldValue: old,
                              newValue: "leadgen",
                            },
                          ],
                        );
                      }
                    }}
                    className={`flex-1 py-1.5 text-[10px] font-bold uppercase rounded-md transition-all ${websiteType === "leadgen" ? "bg-white dark:bg-slate-700 shadow text-blue-600 dark:text-white" : "text-gray-500"}`}
                  >
                    Lead
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                  Project Board
                </label>
                <button
                  onClick={() => {
                    setWantsNotion(!wantsNotion);
                    savePartial({ wantsNotion: !wantsNotion });
                  }}
                  className={`w-full py-2 text-[10px] font-bold uppercase rounded-lg border transition-all ${wantsNotion ? "bg-blue-50 border-blue-200 text-blue-700" : "bg-white border-gray-200 text-gray-400"}`}
                >
                  {wantsNotion ? "Notion Required" : "No Notion"}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                Container ID
              </label>
              <div className="relative">
                <Server
                  size={14}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
                <input
                  className="w-full pl-9 text-sm border border-gray-200 dark:border-slate-700 rounded-lg py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  placeholder="Container ID"
                  value={containerId}
                  onChange={(e) =>
                    handleTextChange(
                      setContainerId,
                      "containerId",
                      e.target.value,
                    )
                  }
                  onBlur={(e) => {
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: clientName || "Unknown",
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "containerId",
                          oldValue: null,
                          newValue: e.target.value,
                        },
                      ],
                    );
                  }}
                />
              </div>
            </div>

            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                Data Classification
              </label>
              <div className="relative">
                <Shield
                  size={14}
                  className={`absolute left-3 top-2.5 ${
                    dataClassification === "internal"
                      ? "text-orange-500"
                      : dataClassification === "confidential"
                        ? "text-red-500"
                        : dataClassification === "public"
                          ? "text-green-500"
                          : "text-gray-400"
                  }`}
                />
                <select
                  value={dataClassification}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDataClassification(val);
                    savePartial({ dataClassification: val });
                    logAuditAction(
                      "UPDATE",
                      {
                        collection: "onboarding_cases",
                        id: currentCaseId || "",
                        name: clientName || "Unknown",
                      },
                      {
                        uid: user?.uid || "unknown",
                        email: user?.email || "unknown",
                      },
                      [
                        {
                          field: "dataClassification",
                          oldValue: dataClassification,
                          newValue: val,
                        },
                      ],
                    );
                  }}
                  className={`w-full pl-9 pr-3 text-sm border border-gray-200 dark:border-slate-700 rounded-lg py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all font-bold appearance-none cursor-pointer ${
                    dataClassification === "internal"
                      ? "text-orange-500"
                      : dataClassification === "confidential"
                        ? "text-red-500"
                        : dataClassification === "public"
                          ? "text-green-500"
                          : ""
                  }`}
                >
                  <option
                    value="internal"
                    className="text-orange-500 font-bold"
                  >
                    Internal
                  </option>
                  <option
                    value="confidential"
                    className="text-red-500 font-bold"
                  >
                    Confidential
                  </option>
                  <option value="public" className="text-green-500 font-bold">
                    Public
                  </option>
                </select>
                <div className="absolute right-3 top-2.5 pointer-events-none text-gray-400">
                  <svg
                    width="10"
                    height="6"
                    viewBox="0 0 10 6"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1 1L5 5L9 1"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: WEB DEV + BILLING */}
      <div className="flex flex-col gap-6">
        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-5 flex items-center gap-2 pb-2 border-b dark:border-slate-700">
            <User size={14} /> Web Developer
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <input
              className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
              placeholder="Dev Name"
              value={webDevDetails.name}
              onChange={(e) => {
                setWebDevDetails({
                  ...webDevDetails,
                  name: e.target.value,
                });
                debouncedSave("webDevDetails.name", e.target.value);
              }}
              onBlur={(e) => {
                logAuditAction(
                  "UPDATE",
                  {
                    collection: "onboarding_cases",
                    id: currentCaseId || "",
                    name: clientName || "Unknown",
                  },
                  {
                    uid: user?.uid || "unknown",
                    email: user?.email || "unknown",
                  },
                  [
                    {
                      field: "webDevName",
                      oldValue: null,
                      newValue: e.target.value,
                    },
                  ],
                );
              }}
            />
            <input
              className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
              placeholder="Dev Email"
              value={webDevDetails.email}
              onChange={(e) => {
                setWebDevDetails({
                  ...webDevDetails,
                  email: e.target.value,
                });
                debouncedSave("webDevDetails.email", e.target.value);
              }}
              onBlur={(e) => {
                logAuditAction(
                  "UPDATE",
                  {
                    collection: "onboarding_cases",
                    id: currentCaseId || "",
                    name: clientName || "Unknown",
                  },
                  {
                    uid: user?.uid || "unknown",
                    email: user?.email || "unknown",
                  },
                  [
                    {
                      field: "webDevEmail",
                      oldValue: null,
                      newValue: e.target.value,
                    },
                  ],
                );
              }}
            />
          </div>
        </div>

        {/* Billing Card */}
        <div className="bg-white dark:bg-slate-800 p-5 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 flex-1 flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider flex items-center gap-2">
              <ReceiptEuro size={14} /> Billing
            </h3>
            <select
              value={billingMethod}
              onChange={(e) => {
                setBillingMethod(e.target.value);
                savePartial({ "billing.method": e.target.value });
              }}
              className="text-xs bg-slate-100 dark:bg-slate-700 border-none rounded p-1 dark:text-white outline-none cursor-pointer"
            >
              <option value="default">Default</option>
              <option value="manual">Manual</option>
            </select>
          </div>

          <div className="space-y-4 flex-1 flex flex-col">
            {/* Chargebee Customer ID */}
            <div>
              <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                Chargebee Customer ID
              </label>
              <div className="relative">
                <CreditCard
                  size={14}
                  className="absolute left-3 top-2.5 text-gray-400"
                />
                <input
                  className="w-full pl-9 text-sm border border-gray-200 dark:border-slate-700 rounded-lg py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                  placeholder="Chargebee ID (e.g. 123456)"
                  value={chargebeeCustomerId || ""}
                  onChange={(e) =>
                    handleTextChange(
                      setChargebeeCustomerId,
                      "chargebeeCustomerId",
                      e.target.value,
                    )
                  }
                />
              </div>
            </div>

            {billingMethod === "manual" && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                      Billing Name
                    </label>
                    <div className="relative">
                      <User
                        size={14}
                        className="absolute left-3 top-2.5 text-gray-400"
                      />
                      <input
                        className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                        placeholder="Billing Name"
                        value={billingDetails.contactPerson}
                        onChange={(e) =>
                          handleBillingChange("contactPerson", e.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                      Billing Email
                    </label>
                    <div className="relative">
                      <Globe
                        size={14}
                        className="absolute left-3 top-2.5 text-gray-400"
                      />
                      <input
                        className="w-full text-sm border border-gray-200 dark:border-slate-700 rounded-lg pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900/50 dark:text-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all"
                        placeholder="Billing Email"
                        value={billingDetails.email}
                        onChange={(e) =>
                          handleBillingChange("email", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="flex-1 flex flex-col">
                  <label className="text-[10px] uppercase font-bold text-gray-400 mb-1.5 block">
                    Billing Address & PO
                  </label>
                  <textarea
                    className="w-full text-sm border p-2 rounded dark:bg-slate-900 dark:border-slate-600 dark:text-white resize-none flex-1"
                    placeholder="Address, VAT, PO..."
                    value={billingDetails.address}
                    onChange={(e) =>
                      handleBillingChange("address", e.target.value)
                    }
                    onBlur={(e) => {
                      if (e.target.value) {
                        logAuditAction(
                          "UPDATE",
                          {
                            collection: "onboarding_cases",
                            id: currentCaseId || "",
                            name: clientName || "Unknown",
                          },
                          {
                            uid: user?.uid || "unknown",
                            email: user?.email || "unknown",
                          },
                          [
                            {
                              field: "billingAddress",
                              oldValue: null,
                              newValue: "Updated Address",
                            },
                          ],
                        );
                      }
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
